import React, {
	useState,
	useMemo,
} from 'react';
import {
	useMutation,
	useQuery,
} from 'react-query';
import {
	Checkbox,
	Flex,
	Typography,
	Table as AntdTable,
} from 'antd';
import { Moment } from 'moment';
import { DATE_AND_TIME, DemurrageTerms } from '@shared/utils/constants';
import {
	formatDuration,
	getUtcOffset,
	toMoment,
} from '@shared/utils/date';
import { sortByDates } from '@shared/utils/sortByDates';
import type { CreateExclusionItemRequest } from '@api/features/exclusions/createExclusionItem';
import type { ExclusionItemProps } from '@api/models/exclusion-item';
import type { CargoPortWithTurnDays } from '@api/features/laytime/getCargosAndLaytimes';
import Card from '@client/components/Card/Card';
import EditableTable, { EditableColumn } from '@client/components/EditableTable/index';
import { EditableCell } from '@client/components/EditableTable/EditableCell';
import {
	createExclusionItem,
	deleteExclusionItem,
	getExclusionItems,
	updateExclusionItem,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import { formatDate } from '@client/utils/formatDate';
import CountryFlag from '@client/components/CountryFlag';
import styles from './Exclusions.module.css';

type Props = {
	demurrageTerm: DemurrageTerms;
	selectedCargoPort: CargoPortWithTurnDays;
	setExclusionsChanged: React.Dispatch<React.SetStateAction<boolean>>;
};

type LegendProps = {
	items: Array<{ label: string; color: string }>;
};

const Legend = ({ items }: LegendProps) => {
	return (
		<Flex justify="flex-end" align="center" gap={10}>
			{items.map((i) => (
				<Flex gap={5} justify="center" align="center">
					<Typography.Text>
						{i.label}
					</Typography.Text>
					<div style={{ width: 25, height: 25, backgroundColor: i.color, borderRadius: 5 }} />
				</Flex>
			))}
		</Flex>
	);
};

const Exclusions = ({
	selectedCargoPort,
	demurrageTerm,
	setExclusionsChanged,
}: Props) => {
	const [exclusionGroupId, setExclusionGroupId] = useState<number>();
	const [localExclusionItems, setLocalExclusionItems] = useState<Array<ExclusionItemProps>>([]);
	const defaultUtcOffset = getUtcOffset(selectedCargoPort.port);

	const {
		refetch,
		isLoading,
	} = useQuery(
		['exclusionItems', selectedCargoPort?.id],
		() => getExclusionItems(selectedCargoPort?.id),
		{
			enabled: !!selectedCargoPort,
			onSuccess: (data) => {
				if (data && data.ExclusionItems) {
					setExclusionGroupId(data.id);
					setLocalExclusionItems(data.ExclusionItems);
				}
			},
		},
	);

	const createMutation = useMutation(
		createExclusionItem,
		{
			onSuccess: () => {
				refetch();
				setExclusionsChanged(true);
				showSuccessNotification('Exclusion item created!');
			},
			onError: (e) => {
				showErrorNotification('Exclusion could not be created', e as Error);
			},
		},
	);

	const onAdd = (values: CreateExclusionItemRequest) => {
		if (!exclusionGroupId) {
			return;
		}

		if (selectedCargoPort.laytimeFrom) {
			if (toMoment(values.from).isBefore(selectedCargoPort.laytimeFrom)) {
				showErrorNotification('Exclusion cannot start before laytime starts');

				return;
			}
		}

		if (selectedCargoPort.laytimeTo) {
			if (toMoment(values.to).isAfter(selectedCargoPort.laytimeTo)) {
				showErrorNotification('Exclusion cannot end after laytime ends');

				return;
			}
		}

		createMutation.mutate({
			...values,
			exclusionGroupId,
		});
	};

	const updateMutation = useMutation(
		updateExclusionItem,
		{
			onSuccess: () => {
				refetch();
				setExclusionsChanged(true);
				showSuccessNotification('Exclusion item updated!');
			},
			onError: (e) => {
				showErrorNotification('Exclusion could not be updated', e as Error);
			},
		},
	);

	const onSave = async (id: number, attributes: Partial<ExclusionItemProps>) => {
		if (selectedCargoPort.laytimeFrom && attributes.from) {
			if (toMoment(attributes.from).isBefore(selectedCargoPort.laytimeFrom)) {
				showErrorNotification('Exclusion cannot start before laytime starts');

				return;
			}
		}

		if (selectedCargoPort.laytimeTo && attributes.to) {
			if (toMoment(attributes.to).isAfter(selectedCargoPort.laytimeTo)) {
				showErrorNotification('Exclusion cannot end after laytime ends');

				return;
			}
		}

		updateMutation.mutate({
			id,
			attributes,
		});
	};

	const deleteMutation = useMutation(
		deleteExclusionItem,
		{
			onSuccess: () => {
				refetch();
				setExclusionsChanged(true);
			},
		},
	);

	const onDelete = (id: number) => deleteMutation.mutate(id);

	const columns = useMemo((): Array<EditableColumn<ExclusionItemProps, 'id'>> => {
		return [
			{
				dataIndex: 'description',
				title: 'Description',
				editable: true,
				width: 145,
				editingProps: {
					type: 'string',
					inputProps: {},
				},
			},
			{
				dataIndex: 'comment',
				title: 'Comment',
				editable: true,
				width: 125,
				editingProps: {
					type: 'textarea',
					inputProps: {},
				},
			},
			{
				dataIndex: 'from',
				title: 'Exclusion start',
				editable: true,
				width: 150,
				editingProps: {
					type: 'date',
					inputProps: {
						time: true,
						defaultUtcOffset,
						showTimezone: false,
						defaultPickerValue: selectedCargoPort.defaultDate ?? undefined,
					},
				},
				render: (v: Moment) => formatDate(
					v, 
					DATE_AND_TIME, 
					false
				),
			},
			{
				dataIndex: 'to',
				title: 'Exclusion end',
				editable: true,
				width: 150,
				editingProps: {
					type: 'date',
					inputProps: {
						time: true,
						defaultUtcOffset,
						showTimezone: false,
						defaultPickerValue: selectedCargoPort.defaultDate ?? undefined,
					},
				},
				render: (v: Moment) => formatDate(v, DATE_AND_TIME, false),
			},
			{
				dataIndex: 'eventTimeTotal',
				title: 'Event time total',
				width: 125,
				render: (_: any, row: ExclusionItemProps) => {
					if (!row.from && !row.to) {
						return 'N/A';
					}

					return formatDuration({ rawStart: row.from, rawEnd: row.to });
				},
			},
			{
				dataIndex: 'percentExcluded',
				title: '% excluded',
				editable: true,
				width: 100,
				editingProps: {
					type: 'number',
					defaultValue: 100,
					inputProps: {
						min: 0,
						max: 100,
					},
				},
				render: (v: number) => `${v}%`,
			},
			{
				dataIndex: 'overrideSHEX',
				title: 'Override SHEX',
				editable: true,
				width: 50,
				editingProps: {
					type: 'check',
					inputProps: {},
				},
				render: (v: boolean) => (<Checkbox checked={v} />),
			},
		];
	}, [defaultUtcOffset, selectedCargoPort]);

	const totalTimeDeducted = useMemo(() => {
		let timeInMinutes = 0;

		localExclusionItems.forEach((item) => {
			const from = toMoment(item.from);
			const to = toMoment(item.to);

			const duration = to.diff(from, 'minute', true);
			timeInMinutes += (duration * ((100 - (item.percentExcluded ?? 0)) / 100));
		});

		return timeInMinutes;
	}, [localExclusionItems]);

	const legend = useMemo(() => {
		return (
			<Legend
				items={[
					{ label: `During ${demurrageTerm.toLowerCase()}`, color: 'rgb(255, 226, 190)' },
					{ label: 'Overlaps SHEX', color: 'rgb(206, 223, 255)' },
				]}
			/>
		);
	}, []);

	return (
		<Card
			title={(
				<Flex gap={15} align="center">
					<p style={{ margin: 0 }}>Exclusions</p>
					<Flex gap={5}>
						<CountryFlag countryCode={selectedCargoPort?.port.countryCode} />
						<p style={{ margin: 0 }}>{`${selectedCargoPort?.port.name}`}</p>
					</Flex>
				</Flex>
			)}
			padding={0}
			size="small"
		>
			<EditableTable<ExclusionItemProps, 'id'>
				size="small"
				allowAddNew
				addNewText="Add exclusion"
				onAddNew={onAdd}
				addNewContainerClassName={styles.addNewContainer}
				onDelete={onDelete}
				columns={columns}
				iconButtons
				emptyText="No exclusions added yet"
				components={{
					body: {
						cell: EditableCell,
					},
				}}
				keyDataIndex="id"
				onSave={onSave}
				dataSource={sortByDates(localExclusionItems, 'from')}
				rowClassName={(row: ExclusionItemProps) => {
					if (row.overlapsWithSHEX) {
						return styles.shexOverlapRow;
					}

					if (row.isDuringDemurrage) {
						return styles.isDuringDemurrageRow;
					}

					return '';
				}}
				pagination={false}
				loading={isLoading}
				enableDelete
				addRowExtraContent={legend}
				summary={() => (
					<AntdTable.Summary.Row>
						<AntdTable.Summary.Cell index={0} colSpan={columns.length + 1}>
							<Flex justify="space-between" align="center">
								<b>Total time deducted</b>
								<b>
									{formatDuration({
										rawMinutes: totalTimeDeducted,
									})}
								</b>
							</Flex>
						</AntdTable.Summary.Cell>
					</AntdTable.Summary.Row>
				)}
			/>
		</Card>
	);
};

export default Exclusions;
